import React, { useState } from 'react'
import { Button, FontIcon, tokens } from '@arvoreeducacao/bonsai-ds'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import useEntityClasses from 'utils/hooks/use-entity-classes'
import type { EntityTypes } from 'api/data.types'
import type { HierarchyStepProps } from '../NewReadingGoalModal.types'
import SpinnerLoader from 'components/loader'
import {
  Wrapper,
  PanelsWrapper,
  Panel,
  PanelHeader,
  PanelTitle,
  BackButton,
  SearchWrapper,
  SearchContainer,
  SearchInput,
  ListWrapper,
  ListRow,
  ListRowText,
  DegreeTag,
  SelectedPanel,
  SelectedPanelTitle,
  SelectedListWrapper,
  SelectedRow,
  SelectedRowName,
  RemoveButton,
  EmptyStateWrapper,
  EmptyStateTitle,
  EmptyStateSubtitle,
  ButtonWrapper
} from './HierarchyStep.styles'

type NavNode = {
  id: number
  name: string
  type: EntityTypes
}

const formatDegree = (
  degree: string | number | undefined
): string | undefined => {
  if (degree == null || degree === '') return undefined
  const num = Number(degree)
  if (!isNaN(num)) return `${num}º ano`
  return String(degree)
}

const HierarchyStep: React.FC<HierarchyStepProps> = ({
  selectedClasses,
  handleClassSelection,
  handlePreviousStep,
  handleNextStep
}) => {
  const { user } = useUserInfo()
  const userEntityId = user?.reader?.entity?.id as number
  const userEntityName = user?.reader?.entity?.name as string

  const [navigationPath, setNavigationPath] = useState<NavNode[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  const currentNode = navigationPath.at(-1)
  const currentEntityId = currentNode?.id ?? userEntityId
  const currentEntityName = currentNode?.name ?? userEntityName

  const { classes: children, loading } = useEntityClasses({
    entityId: currentEntityId,
    perPage: 300,
    shouldShowCurrentEntityIfEmpty: false
  })

  const isAtClassLevel = children.length > 0 && children[0].type === 'class'

  const filteredChildren = children.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleDrillIn = (node: NavNode) => {
    setNavigationPath(prev => [...prev, node])
    setSearchQuery('')
  }

  const handleBack = () => {
    setNavigationPath(prev => prev.slice(0, -1))
    setSearchQuery('')
  }

  const toggleClass = (item: { id: number; name: string; degree?: string }) => {
    const isChecked = selectedClasses.some(sc => sc.id === item.id)
    handleClassSelection({
      item: {
        id: item.id,
        label: item.name,
        degree: formatDegree(item.degree)
      },
      updatedCheck: !isChecked
    })
  }

  return (
    <Wrapper>
      <PanelsWrapper>
        <Panel>
          <PanelHeader>
            {navigationPath.length > 0 && (
              <BackButton type="button" onClick={handleBack}>
                <FontIcon
                  iconName="arrow-left"
                  color={tokens.colors.dark5}
                  size={16}
                />
              </BackButton>
            )}
            <PanelTitle>{currentEntityName}</PanelTitle>
          </PanelHeader>
          <SearchWrapper>
            <SearchContainer>
              <FontIcon
                iconName="search"
                color={tokens.colors.dark5}
                size={16}
              />
              <SearchInput
                placeholder="Pesquisar"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
          </SearchWrapper>
          <ListWrapper>
            {loading ? (
              <SpinnerLoader style={{ minHeight: '200px' }} />
            ) : (
              filteredChildren.map(item => {
                const isChecked = selectedClasses.some(sc => sc.id === item.id)

                return (
                  <ListRow
                    key={item.id}
                    onClick={() => {
                      if (isAtClassLevel) {
                        toggleClass(item)
                      } else {
                        handleDrillIn({
                          id: item.id,
                          name: item.name,
                          type: item.type as EntityTypes
                        })
                      }
                    }}
                  >
                    {isAtClassLevel && (
                      <FontIcon
                        iconName={isChecked ? 'check-square-fill' : 'checkbox'}
                        color={
                          isChecked ? tokens.colors.livros : tokens.colors.dark3
                        }
                        size={20}
                      />
                    )}
                    <ListRowText>{item.name}</ListRowText>
                    {isAtClassLevel && item.degree && (
                      <DegreeTag>{formatDegree(item.degree)}</DegreeTag>
                    )}
                    {!isAtClassLevel && (
                      <FontIcon
                        iconName="caret-right"
                        color={tokens.colors.dark4}
                        size={14}
                      />
                    )}
                  </ListRow>
                )
              })
            )}
          </ListWrapper>
        </Panel>

        <SelectedPanel>
          <SelectedPanelTitle>Turmas selecionadas</SelectedPanelTitle>
          {selectedClasses.length === 0 ? (
            <EmptyStateWrapper>
              <EmptyStateTitle>Nenhuma turma selecionada</EmptyStateTitle>
              <EmptyStateSubtitle>
                Os alunos selecionados aparecerão aqui
              </EmptyStateSubtitle>
            </EmptyStateWrapper>
          ) : (
            <SelectedListWrapper>
              {selectedClasses.map(sc => (
                <SelectedRow key={sc.id}>
                  <SelectedRowName>{sc.label}</SelectedRowName>
                  {sc.degree && <DegreeTag>{sc.degree}</DegreeTag>}
                  <RemoveButton
                    type="button"
                    onClick={() =>
                      handleClassSelection({ item: sc, updatedCheck: false })
                    }
                  >
                    <FontIcon
                      iconName="cancel"
                      color={tokens.colors.dark4}
                      size={14}
                    />
                  </RemoveButton>
                </SelectedRow>
              ))}
            </SelectedListWrapper>
          )}
        </SelectedPanel>
      </PanelsWrapper>

      <ButtonWrapper>
        <Button
          theme={{ type: 'secondary', size: 'large' }}
          text="Voltar"
          onClick={handlePreviousStep}
        />
        <Button
          theme={{ type: 'primaryLivros', size: 'large' }}
          text="Próximo"
          disabled={selectedClasses.length === 0}
          onClick={handleNextStep}
        />
      </ButtonWrapper>
    </Wrapper>
  )
}

export default HierarchyStep
